import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {accessibleObjs, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {INACCESSIBLE_STUDY_ERROR_TEXT} from 'studies/typed/shared/analysis/ProcedureRunAnalysisConstants';
import {StudyInaccessibleContentBanner} from 'studies/typed/utils/ui/StudyQueryBanners';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredSpinner} from 'ui-library/Spinner';

import {StudyAnalysisQuery, StudyAnalysisQueryVariables} from './__generated/StudyAnalysisQuery';
import {
  getUnitOperationCountFromProcedureRuns,
  OOTB_STUDY_DESCRIPTION,
  ProcedureRunAnalysisDetails,
} from './ProcedureRunAnalysis';

export const FAILED_TO_LOAD_RUNS_MESSAGE = 'Failed to load run data. Refresh the page and try again.';

export const STUDY_ANALYSIS_QUERY: DocumentNode = gql`
  query StudyAnalysisQuery($id: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    study(id: $id) {
      ... on StudyNode {
        id
        studySchema {
          id
          studyType
        }
        procedureRuns {
          ... on ProcedureRunNode {
            id
            methodExecutionInstances {
              id
            }
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export function SingleStudyAnalysisDetails({studyId}: {studyId: string}): JSX.Element {
  const result = useBenchlingQuery<StudyAnalysisQuery, StudyAnalysisQueryVariables>(STUDY_ANALYSIS_QUERY, {
    variables: {id: studyId},
    renderCachedDataWhenRefetching: true,
  });

  if (result.type === QueryResultType.ERROR) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text={FAILED_TO_LOAD_RUNS_MESSAGE} />;
  }

  if (result.type === QueryResultType.PENDING) {
    return <CenteredSpinner />;
  }

  const {study} = result.data;

  if (isInaccessible(study)) {
    return (
      <StudyInaccessibleContentBanner
        inaccessibleInfo={study}
        modelType={ModelTypeConstants.STUDY}
        displayErrorText={INACCESSIBLE_STUDY_ERROR_TEXT}
      />
    );
  }

  const accessibleRuns = accessibleObjs(study.procedureRuns);
  const hasInaccessibleRuns = accessibleRuns.length !== study.procedureRuns.length;
  const unitOperationCount = getUnitOperationCountFromProcedureRuns(accessibleRuns);

  return (
    <ProcedureRunAnalysisDetails
      studyType={study.studySchema.studyType}
      unitOperationCount={unitOperationCount}
      hasInaccessibleRuns={hasInaccessibleRuns}
      ootbDescription={OOTB_STUDY_DESCRIPTION}
    />
  );
}
